import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { tap, map, first } from 'rxjs/operators';

import { ConfirmDialogComponent } from '@library/common';

import { ErrorMessageService, BreadcrumbService, RecoveryService } from '@library/core/services';
import { ShipmentService } from '@library/modules/shipment';
import { TemplateService } from '../template.service';

import { Template } from '../template';
import { forkJoin } from 'rxjs';
import { UseraccessService } from '@library/modules/user';

@Component( {
   selector: 'lib-template-modify',
   templateUrl: './template-modify.component.html',
   styleUrls: [ './template-modify.component.scss' ]
} )
export class TemplateModifyComponent implements OnInit {

   _template: Template;
   get template(): Template { return this._template; }
   set template( template: Template ) {
      this._template = new Template( template );
      if ( this.template.data ) {
         this.shipment.shipment = this.template.data;
      } else {
         this.shipment.shipmentForm = null;
      }
      if ( this.template.name ) {
         this.breadcrumbs.addCrumble( template.name );
      }
      this.loaded = true;
   }

   hasQuote: boolean = false;
   hasShip: boolean = false;

   loaded = false;
   _saving = false;
   get saving() { return this._saving; }
   set saving( value: boolean ) {
      this._saving = value;
      this.shipment.disabled = value;
   }

   constructor(
      private router: Router,
      private route: ActivatedRoute,
      private breadcrumbs: BreadcrumbService,
      public shipment: ShipmentService,
      public templateService: TemplateService,
      public error: ErrorMessageService,
      public recoveryService: RecoveryService,
      private access: UseraccessService,
      public dialog: MatDialog,
      public snackbar: MatSnackBar,
      public cdr: ChangeDetectorRef,
   ) { }

   ngOnInit() {
      this.shipment.template$.next(true);
      const id = this.route.snapshot.paramMap.get( 'id' );
      const action = this.route.snapshot.paramMap.get( 'action' ) || 'new';

      this.breadcrumbs.newCrumb({ label: 'Templates', path: '/template' });
      this.breadcrumbs.addCrumble( action );

      this.shipment.reset();

      forkJoin([
         this.access.hasAccess('quoting').pipe(first()),
         this.access.hasAccess('booking').pipe(first()),
      ]).subscribe( ( [ q, b]) => {
         this.hasQuote = q;
         this.hasShip = b;

         this.shipment.shipmentUpdate$.next( true );
      });

      switch ( action ) {
         case 'recover':
            this.recoveryService.recoverItem<Template>( id ).pipe(
               map( t => new Template({
                  name: t?.name,
                  data: t?.data,
               })),
            ).subscribe( template => this.template = template );
            break;
         case 'edit':
            this.templateService.clearCached( id );
            this.templateService.getTemplate( id ).subscribe( template => {
               if ( !template.owned ) {
                  this.router.navigate([ '/template' ]);

                  return;
               }

               this.template = template;
            });
            break;
         default:
            this.template = new Template();
      }
   }

   ngOnDestroy() {
      this.shipment.template$.next(false);
   }

   delete( id: number ) {
      this.saving = true;

      const dialogRef = this.dialog.open( ConfirmDialogComponent, {
         data: {
            title: 'Confirm Deletion?',
            msg: 'Are you sure you want to delete this template?',
            yes: 'Delete'
         }
      } );

      dialogRef.afterClosed().subscribe( result => {
         if ( result ) {
            this.templateService.deleteTemplate( id ).subscribe(
               () => {
                  this.router.navigate([ 'template' ]);
               }, error => {
                  console.log( 'error', error );
                  this.router.navigate([ 'template' ]);
               });
         } else {
            this.saving = false;
         }
      });
   }

   save() {
      if ( !this.template.name ) { return; }

      this.template.data = this.shipment.shipmentForm.getRawValue();

      this.saving = true; // make sure this line comes after the getRawValue()

      this.templateService.saveTemplate( this.template ).pipe(
         tap( _ => this.saving = false ),
      ).subscribe(
         _ => {
            this.snackbar.open( 'Template saved!', '', { duration: 5000 });
            this.router.navigate([ '/template' ]);
         },
         error => {
            console.log( 'template save error', error );
            this.snackbar.open( 'Template Save Error', 'Ok' );
         }
      );
   }

   createShipment( id ) {
      this.shipment.template$.next(false);
      this.templateService.clearCached( id );
      this.templateService.getTemplate( id ).subscribe( t => {
         if ( t ) { this.shipment.shipment = t.data; }
         this.router.navigate([ '/create/shipment/new' ]);
      });
   }

   createQuotation( id ) {
      this.shipment.template$.next(false);
      this.templateService.clearCached( id );
      this.templateService.getTemplate( id ).subscribe( t => {
         if ( t ) { this.shipment.shipment = t.data; }
         this.router.navigate([ '/create/quotation/new' ]);
      });
   }
}
